import type { SkeletonProps } from "@mantine/core";

import { CopyButton, SkeletonPlaceholder } from "@expert/common-ui";
import { Text } from "@mantine/core";

import type { ChatTask } from "@/sdk";

import { DEFAULT_COUNTRY_CODE, formatPhoneNumber, getMdnClipboardValue, validatePhoneNumber } from "@/shared-utils";

// 20 is the true max but 30 is to account for edge case errors states.
const MAX_MDN_LENGTH = 30;

interface CustomerMdn extends Pick<ChatTask, "mdn">, SkeletonProps {
    showCopyMdnIcon: boolean;
}

export const CustomerMdn = ({ mdn, showCopyMdnIcon, ...skeletonProps }: CustomerMdn) => {
    const rawValue = mdn.value;
    const isValidString = typeof rawValue === "string" && rawValue.length > 0;
    const hasValidMdn = !mdn.error && isValidString;
    const isRecognizedPhone = hasValidMdn && validatePhoneNumber(rawValue).valid;
    const displaySource = isRecognizedPhone
        ? formatPhoneNumber(rawValue, DEFAULT_COUNTRY_CODE, false).number
        : (rawValue ?? "");
    const isTruncated = isValidString && displaySource.length > MAX_MDN_LENGTH;
    const truncatedDisplay = isTruncated ? `${displaySource.slice(0, MAX_MDN_LENGTH)}…` : displaySource;
    const displayText = hasValidMdn ? truncatedDisplay : "Number unavailable";

    return (
        <SkeletonPlaceholder visible={mdn.loading} {...skeletonProps}>
            <Text
                span
                className="fs-mask"
                size="sm"
                style={{ whiteSpace: "nowrap" }}
                truncate={false}
                title={hasValidMdn && displaySource.length > MAX_MDN_LENGTH ? rawValue : undefined}
            >
                {displayText}
            </Text>
            {showCopyMdnIcon && hasValidMdn && (
                <CopyButton
                    value={getMdnClipboardValue(rawValue)}
                    showTooltip={{ copiedLabel: "Copied", copyLabel: "Copy number" }}
                    checkMarkOverride={{ fill: "var(--mantine-color-dark-2)" }}
                    copyIconOverride={{ stroke: "var(--mantine-color-dark-2)" }}
                />
            )}
        </SkeletonPlaceholder>
    );
};
